import {
  FC, useCallback, useMemo, useState,
} from 'react';
import Modal from 'src/common/components/ModalDS22';
import { Field, useForm, type FieldInputProps } from 'react-final-form';
import Typography from 'src/common/components/Typography';
import CloseButton from 'src/common/components/CloseButton';
import Datepicker from 'src/common/components/Datepicker';
import newTZDate, { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import {
  CAPTAINED, DEPARTURE_DATE_FIELD, DEPARTURE_TIME_FIELD, DURATION_FIELD,
  type BookNowDirectFormState,
} from 'src/book-now-direct/constants';
import DatesField from 'src/common/components/DatesField';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import { add, isSameMonth, startOfToday } from 'date-fns';
import { useTranslation } from 'next-i18next';
import InstabookOptionsList from 'src/book-now-direct/components/InstabookOptionsList';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import Button from 'src/common/components/ButtonDS22';
import HighlightBanner from 'src/common/components/HighlightBanner';
import type { Motivator } from 'src/common/types/api/v4/bookingMotivators';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import classNames from 'classnames';
import DayContent from 'src/common/components/DayContent/DayContent';
import styles from './DatePickerAndModal.module.scss';

type Props = {
  selectedMotivators: Motivator[];
  daysUntilInstabook?: number;
};

const DatePickerAndModal: FC<Props> = ({
  selectedMotivators,
  daysUntilInstabook,
}) => {
  const { batch, change, getState } = useForm<BookNowDirectFormState>();
  const { values: { adults, departDate } } = getState();
  const [open, setOpen] = useState(!departDate); // auto-open if initial date not provided
  const [tListing] = useTranslation('listing-page');
  const [tDirect] = useTranslation('book-now-direct');
  const {
    fetchAndUpdateInstabooksOnDate,
    fetchInstabookDatesForMonth,
    instabookDates,
    previewInstabooksOnDate,
    commitPreviewInstabooks,
    listing,
    loading,
    updateBlockedTimesForDate,
  } = useBookNowDirectContext();
  const {
    bookingMotivators,
  } = useBookingMotivatorsContext();

  /** Interacting with calendar should not set Form date until commiting */
  const [localDate, setLocalDate] = useState(departDate);

  /** Fetch instabooks on day, but do not change form state yet  */
  const onSelectCalendarDate = useCallback(async (date: Date) => {
    setLocalDate(date);
    if (adults) {
      fetchAndUpdateInstabooksOnDate(date, adults);
    }
    updateBlockedTimesForDate(listing.id, date);
  }, [
    fetchAndUpdateInstabooksOnDate,
    adults,
    updateBlockedTimesForDate,
    listing.id,
  ]);

  /** No instabooks: listing not enabled, or enabled but API returned no available dates */
  const hasNoInstabooks = !listing.instabook_enabled
    || (listing.instabook_enabled && !instabookDates?.length);

  const closeModal = useCallback(() => {
    if (hasNoInstabooks) return;
    setOpen(false);
  }, [hasNoInstabooks]);

  /** If a specific instabook option was not clicked, select the cheapest from the current date */
  const selectCheapest = useCallback(() => {
    if (previewInstabooksOnDate.length) {
      const instabook = previewInstabooksOnDate[0];
      const instabookDate = instabook?.date
        ? newTZDate(instabook.date)
        : departDate;
      batch(() => {
        change(DEPARTURE_DATE_FIELD, instabookDate);
        change(DURATION_FIELD, instabook.trip_length);
        change(CAPTAINED, instabook.captained);
        if (previewInstabooksOnDate.length === 1) {
          change(DEPARTURE_TIME_FIELD, instabook.instabook_trip_times[0].min_start_time);
        }
      });
    }
    commitPreviewInstabooks();
    closeModal();
  }, [previewInstabooksOnDate, departDate, commitPreviewInstabooks, closeModal, batch, change]);

  const dateFnsLocale = useDateFnsLocale();

  const instabookDatesAsStrings = instabookDates?.map((dateString) => newTZDate(dateString));
  const startDate = departDate || (
    instabookDatesAsStrings?.length ? instabookDatesAsStrings[0] : undefined
  );
  const departDateAsString = departDate ? formatDate(departDate) : null;

  const [visibleMonth, setVisibleMonth] = useState<Date>(startDate || todayDate());
  const showInstabookOptionsList = useMemo(() => Boolean(
    previewInstabooksOnDate?.[0]?.date
      && isSameMonth(newTZDate(previewInstabooksOnDate[0].date), visibleMonth),
  ), [previewInstabooksOnDate, visibleMonth]);

  const onMonthChange = useCallback((
    input: FieldInputProps<Date, HTMLElement>,
  ) => async (date: Date) => {
    setVisibleMonth(date);
    const res = await fetchInstabookDatesForMonth(date);
    if (res.length) {
      const firstInstabookOfMonth = newTZDate(res[0]);
      input.onChange(firstInstabookOfMonth);
      if (adults) {
        fetchAndUpdateInstabooksOnDate(firstInstabookOfMonth, adults);
      }
    }
  }, [fetchInstabookDatesForMonth, fetchAndUpdateInstabooksOnDate, adults]);

  const isNotInstabook = useCallback((date?: Date) => (
    date && !instabookDates.includes(formatDate(date))
  ), [instabookDates]);

  return (
    <>
      <div className={styles.dateInput}>
        <DatesField
          startDate={departDateAsString}
          endDate={departDateAsString}
          label={tListing('trip-cart--date')}
          labelSize="m"
          required={false}
          handleClick={() => setOpen(true)}
        />
      </div>
      <Modal
        label="Book-now-direct-calendar"
        modalOpen={open}
        closeModal={selectCheapest}
        withFocusOutline={false}
        position="right"
        size="medium"
        mobileFullScreen
        closeTimeoutMS={100}
      >
        <div className={styles.modal}>
          <div className={styles.header}>
            <Typography
              size="heading-4"
              type="p"
            >
              {tDirect('booking-options')}
            </Typography>
            <CloseButton size="large" onClick={closeModal} />
          </div>
          <div className={styles.content}>
            {hasNoInstabooks && (
              <HighlightBanner
                title={tDirect('none-instabooks-title')}
                icon="warning"
                variant="warning"
                body={tDirect('none-instabooks-subtext')}
              />
            )}
            <div className={styles.calendar}>
              <Field name={DEPARTURE_DATE_FIELD}>
                {({ input }) => (
                  <Datepicker
                    id="book-now-direct-calendar"
                    classNameModifier="listingTripDetails"
                    startDate={startDate}
                    value={localDate || null}
                    inline
                    focusSelectedMonth
                    minDate={startOfToday()}
                    maxDate={add(todayDate(), { years: 1 })}
                    monthsShown={1}
                    openToDate={departDate ?? startDate}
                    onMonthChange={onMonthChange(input)}
                    onChange={onSelectCalendarDate}
                    dayClassName={(date) => {
                      if (isNotInstabook(date)) {
                        return classNames(styles.unavailable);
                      }
                      return null;
                    }}
                    renderDayContents={(day, date) => (
                      isNotInstabook(date) ? day : (
                        <DayContent
                          bookingMotivators={bookingMotivators}
                          date={date}
                          day={day}
                        />
                      ))}
                  />
                )}
              </Field>
            </div>
            {showInstabookOptionsList && (
              <>
                <div>
                  {previewInstabooksOnDate?.[0] && (
                    <Button
                      onClick={selectCheapest}
                      variant="instabook-green"
                      disabled={loading}
                    >
                      {`${tDirect('select')} ${formatDate(
                        // Ensure we always have a valid Date object
                        previewInstabooksOnDate[0]?.date
                          ? newTZDate(previewInstabooksOnDate[0].date)
                          : (departDate || todayDate()),
                        'E d MMM',
                        dateFnsLocale,
                      )}`}
                    </Button>
                  )}
                </div>
                <InstabookOptionsList
                  closeModal={closeModal}
                  selectedMotivators={selectedMotivators}
                  daysUntilInstabook={daysUntilInstabook}
                />
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DatePickerAndModal;
